
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const units = [
  { id: 'j', name: 'Joules (J)' },
  { id: 'kj', name: 'Kilojoules (kJ)' },
  { id: 'cal', name: 'Calories (cal)' },
  { id: 'kcal', name: 'Kilocalories (kcal)' },
  { id: 'wh', name: 'Watt-hours (Wh)' },
  { id: 'kwh', name: 'Kilowatt-hours (kWh)' },
  { id: 'mwh', name: 'Megawatt-hours (MWh)' },
  { id: 'btu', name: 'British Thermal Units (BTU)' },
  { id: 'therm', name: 'Therms' },
  { id: 'ftlb', name: 'Foot-pounds (ft⋅lb)' },
  { id: 'ev', name: 'Electronvolts (eV)' },
  { id: 'mev', name: 'Megaelectronvolts (MeV)' },
  { id: 'erg', name: 'Ergs' },
];

// Conversion rates to Joules
const conversionRates: Record<string, number> = {
  'j': 1,
  'kj': 1000,
  'cal': 4.184,
  'kcal': 4184,
  'wh': 3600,
  'kwh': 3600000,
  'mwh': 3600000000,
  'btu': 1055.06,
  'therm': 105506000,
  'ftlb': 1.355818,
  'ev': 1.602176634e-19,
  'mev': 1.602176634e-13,
  'erg': 1e-7,
};

const EnergyConverter = () => {
  const [fromValue, setFromValue] = useState<string>('1');
  const [fromUnit, setFromUnit] = useState<string>('kj');
  const [toUnit, setToUnit] = useState<string>('kcal');
  const [toValue, setToValue] = useState<string>('');

  useEffect(() => {
    convert();
  }, [fromValue, fromUnit, toUnit]);

  const convert = () => {
    try {
      const inputValue = parseFloat(fromValue);
      if (!isNaN(inputValue)) {
        // Convert to base unit (Joules)
        const baseValue = inputValue * conversionRates[fromUnit];
        // Convert from base unit to target unit
        const result = baseValue / conversionRates[toUnit];
        setToValue(result.toFixed(10).replace(/\.?0+$/, ""));
      } else {
        setToValue('');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setToValue('Error');
    }
  };

  const handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
  };

  const handleFromUnitChange = (value: string) => {
    setFromUnit(value);
  };

  const handleToUnitChange = (value: string) => {
    setToUnit(value);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Energy Converter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="number" 
                value={fromValue} 
                onChange={handleFromValueChange} 
                className="col-span-2"
              />
              <Select value={fromUnit} onValueChange={handleFromUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button 
              onClick={swapUnits}
              className="p-2 rounded-full hover:bg-muted"
            >
              ⇅
            </button>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="text" 
                value={toValue} 
                readOnly 
                className="col-span-2"
              />
              <Select value={toUnit} onValueChange={handleToUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnergyConverter;
